import React, { useRef, useState, Suspense, useEffect, ReactNode, useMemo, Component, useCallback, useContext } from 'react';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { OrbitControls, useGLTF, Center, PerspectiveCamera, Text, Billboard } from '@react-three/drei';
import * as THREE from 'three';
import { useTranslation } from 'react-i18next';
import './ModelPreview.css';
import { api } from '../services/api';
import axios from 'axios';
import '../styles/pagination.css'; // Import the pagination styles
import { GCodePreviewCard, GCodeFile } from './GCodePreviewCard';
import { extractGCodeFiles } from '../utils/gcodeUtils';
import { GCodeSettingsPopup, type GCodeSettings } from './GCodeSettingsPopup';
import { UserIntentPopup, type UserIntent } from './UserIntentPopup';
import { BuildPlateWarningPopup } from './BuildPlateWarningPopup';
import { GCodeFolderCard, GCodeFolder } from './GCodeFolderCard';
import { PricingPopup } from './PricingPopup';
import { auth } from '../services/firebase';
import { ModelInfo, ModelVersion, ModelCache } from './ModelPreviewComponents/index';
import { formatSize, formatDimension, parseDimension } from './ModelPreviewComponents/formatters';
import { isValidURL } from './ModelPreviewComponents/validators';
import { ModelCacheContext } from './ModelPreviewComponents/ModelCacheContext';
import { ModelBoundsContext } from './ModelPreviewComponents/ModelBoundsContext';
import { CameraAdjust } from './ModelPreviewComponents/CameraAdjust';
import { WireframeOpacity } from './ModelPreviewComponents/WireframeOpacity';
import { CustomBox3Helper } from './ModelPreviewComponents/CustomBox3Helper';
import { MemoizedModel } from './ModelPreviewComponents/Model';
import { 
  formatDimensionWithUnits as formatDimensionWithUnitsUtil,
  parseDimensionWithUnits as parseDimensionWithUnitsUtil,
  getDimensionDisplayValue as getDimensionDisplayValueUtil,
  isValueChanged as isValueChangedUtil
} from './ModelPreviewComponents/ModelCardComponents/dimensionUtils';
import { createDimensionEventHandlers } from './ModelPreviewComponents/ModelCardComponents/dimensionEventHandlers';
import { 
  getExtendedDimensionsFromStorage as getExtendedDimensionsFromStorageUtil,
  saveModelChanges as saveModelChangesUtil,
  resetOriginalValues as resetOriginalValuesUtil,
  getActiveVersionName as getActiveVersionNameUtil
} from './ModelPreviewComponents/ModelCardComponents/modelStorageUtils';
import { 
  downloadFile as downloadFileUtil,
  handleError as handleErrorUtil,
  handleCanvasError as handleCanvasErrorUtil,
  createWebGLContextErrorHandler
} from './ModelPreviewComponents/ModelCardComponents/fileDownloadUtils';
import { 
  initializeDuplicateCount,
  handleDuplicateChange as handleDuplicateChangeUtil,
  handleDuplicateIncrement as handleDuplicateIncrementUtil,
  handleDuplicateDecrement as handleDuplicateDecrementUtil
} from './ModelPreviewComponents/ModelCardComponents/duplicateCountUtils';
import { 
  resetView as resetViewUtil,
  createViewportObserver,
  CameraControlsRef
} from './ModelPreviewComponents/ModelCardComponents/cameraControlUtils';
import ModelCard from './ModelPreviewComponents/ModelCardComponents/ModelCard';


// Create a model cache context
interface ModelCacheType {
  models: Record<string, THREE.Group | null>;
  urls: Record<string, string>;
}

// Simple ErrorBoundary class component
class ModelErrorBoundary extends Component<{children: ReactNode, fallback: ReactNode}> {
  state = { hasError: false };
  
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  
  componentDidCatch(error: Error) {
    console.error("Error caught by boundary:", error);
  }
  
  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    
    return this.props.children;
  }
}

// This component will auto-adjust the camera to fit the model

// Add a new component to handle wireframe opacity

// Update the Model component to use this new component

// Helper component to visualize the 3D bounding box

// Component that manages dimension lines display after model positioning
function BoundingBoxManager({ children }: { children: ReactNode }) {
  const { scene } = useThree();
  const [modelBbox, setModelBbox] = useState<THREE.Box3 | null>(null);
  
  useEffect(() => {
    // Wait a bit for CameraAdjust to position the model properly
    const timer = setTimeout(() => {
      if (scene.children.length > 0) {
        try {
          // Calculate bounding box for the current scene
          const box = new THREE.Box3().setFromObject(scene);
          
          // Only update if the box is valid and has actual size
          const size = box.getSize(new THREE.Vector3());
          if (size.length() > 0) {
            setModelBbox(box.clone());
          }
        } catch (error) {
          console.error("Error calculating bounding box:", error);
        }
      }
    }, 200); // Small delay to ensure model is positioned
    
    return () => clearTimeout(timer);
  }, [scene]);
  
  return (
    <>
      {children}
      {modelBbox && (
        <DimensionLines box={modelBbox} />
      )}
    </>
  );
}

// Component to render dimension lines along X, Y, Z axes
function DimensionLines({ box }: { box: THREE.Box3 }): React.JSX.Element {
  const lines = useMemo(() => {
    const size = new THREE.Vector3();
    box.getSize(size);
    const center = new THREE.Vector3();
    box.getCenter(center);
    const min = box.min;
    const max = box.max;
    
    const lineObjects: THREE.Group[] = [];
    
    // Create dimension lines for each axis
    const createDimensionLine = (
      start: THREE.Vector3, 
      end: THREE.Vector3, 
      color: string, 
      label: string,
      dimension: number
    ) => {
      const group = new THREE.Group();
      
      // Main dimension line
      const geometry = new THREE.BufferGeometry().setFromPoints([start, end]);
      const material = new THREE.LineBasicMaterial({
        color: new THREE.Color(color),
        linewidth: 2,
        transparent: true,
        opacity: 0.3
      });
      const line = new THREE.Line(geometry, material);
      group.add(line);
      
      // Small perpendicular lines at ends (tick marks)
      const tickLength = Math.max(size.x, size.y, size.z) * 0.02; // 2% of largest dimension
      
      // Start tick
      const startTick = createTickMark(start, end, tickLength, color);
      group.add(startTick);
      
      // End tick
      const endTick = createTickMark(end, start, tickLength, color);
      group.add(endTick);
      
      return group;
    };
    
    const createTickMark = (
      position: THREE.Vector3, 
      direction: THREE.Vector3, 
      length: number, 
      color: string
    ) => {
      const dir = direction.clone().sub(position).normalize();
      const perpendicular = new THREE.Vector3();
      
      // Find a perpendicular vector
      if (Math.abs(dir.x) < 0.9) {
        perpendicular.set(1, 0, 0);
      } else {
        perpendicular.set(0, 1, 0);
      }
      perpendicular.cross(dir).normalize().multiplyScalar(length);
      
      const tickStart = position.clone().add(perpendicular);
      const tickEnd = position.clone().sub(perpendicular);
      
      const tickGeometry = new THREE.BufferGeometry().setFromPoints([tickStart, tickEnd]);
      const tickMaterial = new THREE.LineBasicMaterial({
        color: new THREE.Color(color),
        linewidth: 2,
        transparent: true,
        opacity: 0.3
      });
      
      return new THREE.Line(tickGeometry, tickMaterial);
    };
    
    // X-axis (Width) - Red line
    const widthStart = new THREE.Vector3(min.x, min.y - size.y * 0.1, min.z);
    const widthEnd = new THREE.Vector3(max.x, min.y - size.y * 0.1, min.z);
    const widthLine = createDimensionLine(widthStart, widthEnd, '#cc8888', 'W', size.x);
    lineObjects.push(widthLine);
    
    // Y-axis (Depth) - Green line  
    const depthStart = new THREE.Vector3(max.x + size.x * 0.1, min.y, min.z);
    const depthEnd = new THREE.Vector3(max.x + size.x * 0.1, max.y, min.z);
    const depthLine = createDimensionLine(depthStart, depthEnd, '#88cc88', 'D', size.y);
    lineObjects.push(depthLine);
    
    // Z-axis (Height) - Blue line
    const heightStart = new THREE.Vector3(max.x + size.x * 0.1, min.y - size.y * 0.1, min.z);
    const heightEnd = new THREE.Vector3(max.x + size.x * 0.1, min.y - size.y * 0.1, max.z);
    const heightLine = createDimensionLine(heightStart, heightEnd, '#8888cc', 'H', size.z);
    lineObjects.push(heightLine);
    
    return lineObjects;
  }, [box]);

  // Calculate text positions and data
  const textData = useMemo(() => {
    const size = new THREE.Vector3();
    box.getSize(size);
    const min = box.min;
    const max = box.max;
    
    // Calculate font size based on model size
    const fontSize = Math.max(size.x, size.y, size.z) * 0.03;
    
    // Calculate offset for text positioning (so it's not directly on the line)
    const textOffset = Math.max(size.x, size.y, size.z) * 0.03;
    
    return [
      {
        position: [
          (min.x + max.x) / 2, // Center X
          min.y - size.y * 0.1 - textOffset, // Width line Y with offset below
          min.z + textOffset // Width line Z with slight forward offset
        ] as [number, number, number],
        text: 'W',
        color: '#cc8888',
        fontSize
      },
      {
        position: [
          max.x + size.x * 0.1 + textOffset, // Depth line X with offset outward
          (min.y + max.y) / 2, // Center Y
          min.z + textOffset // Depth line Z with slight forward offset
        ] as [number, number, number],
        text: 'D',
        color: '#88cc88',
        fontSize
      },
      {
        position: [
          max.x + size.x * 0.1 + textOffset, // Height line X with offset outward (matching new line position)
          min.y - size.y * 0.1 - textOffset, // Height line Y with offset downward (matching new line position)
          (min.z + max.z) / 2 // Center Z
        ] as [number, number, number],
        text: 'H',
        color: '#8888cc',
        fontSize
      }
    ];
  }, [box]);

  useEffect(() => {
    return () => {
      // Cleanup geometries and materials
      lines.forEach(line => {
        line.traverse((child) => {
          if (child instanceof THREE.Line) {
            if (child.geometry) child.geometry.dispose();
            if (child.material) child.material.dispose();
          }
        });
      });
    };
  }, [lines]);

  return (
    <>
      {lines.map((line, index) => (
        <primitive key={index} object={line} />
      ))}
      {textData.map((data, index) => (
        <Billboard
          key={`billboard-${index}`}
          position={data.position}
        >
          <Text
            fontSize={data.fontSize}
            color={data.color}
            anchorX="center"
            anchorY="middle"
          >
            {data.text}
          </Text>
        </Billboard>
      ))}
    </>
  );
}

// ModelCard component is now imported from separate file

// Main component that displays a grid of model cards for a project
export function ProjectPreview({ 
  projectName, 
  models,
  loading = false,
  onModelsProcessed,
  onCreditsChanged,
  currentCredits
}: { 
  projectName: string; 
  models: ModelInfo[];
  loading?: boolean;
  onModelsProcessed?: () => void;
  onCreditsChanged?: () => void;
  currentCredits?: number | null;
}) {
  const { t } = useTranslation();
  const [selectedModelIds, setSelectedModelIds] = useState<string[]>([])
  const [showOrientationPopup, setShowOrientationPopup] = useState(false)
  const [showUserIntentPopup, setShowUserIntentPopup] = useState(false)
  const [selectedUserIntent, setSelectedUserIntent] = useState<UserIntent>('balanced')
  const [customPrompt, setCustomPrompt] = useState<string>('')
  const [aiSuggestion, setAiSuggestion] = useState<any>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [generationProgress, setGenerationProgress] = useState('')
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)
  const [lastSuccessMessage, setLastSuccessMessage] = useState('')
  const [gcodeResults, setGcodeResults] = useState<any>(null)
  const [showGcodeFiles, setShowGcodeFiles] = useState(false)
  const [isCheckingGcode, setIsCheckingGcode] = useState(false)

  // Session-based tracking for credit deduction
  const getLastVisitTime = (projectName: string): number => {
    const stored = localStorage.getItem(`lastVisit_${projectName}`)
    return stored ? parseInt(stored) : 0
  }

  const updateLastVisitTime = (projectName: string) => {
    localStorage.setItem(`lastVisit_${projectName}`, Date.now().toString())
  }

  // Add state for file upload functionality
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<{[key: string]: number}>({})
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Update last visit time when component unmounts or project changes
  useEffect(() => {
    return () => {
      updateLastVisitTime(projectName)
    }
  }, [projectName])

  // Also update when user navigates away
  useEffect(() => {
    const handleBeforeUnload = () => {
      updateLastVisitTime(projectName)
    }
    
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      updateLastVisitTime(projectName)
    }
  }, [projectName])

  // Preserve the model cache across renders
  const modelCacheRef = useRef<ModelCacheType>({ models: {}, urls: {} });
  const modelCache = modelCacheRef.current;
  const [loadedModelCount, setLoadedModelCount] = useState(0);
  
  // Selection state
  const [allSelected, setAllSelected] = useState(false);
  
  // GCode files state
  const [gcodeFiles, setGcodeFiles] = useState<GCodeFile[]>([]);
  const [gcodeFolders, setGcodeFolders] = useState<GCodeFolder[]>([]);
  const [loadingGcodeFiles, setLoadingGcodeFiles] = useState(false);
  
  // Active tab state
  const [activeTab, setActiveTab] = useState<'models' | 'gcode'>('models');
  
  // Processing state
  const [processingOrientation, setProcessingOrientation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [packingInProgress, setPackingInProgress] = useState(false);
  const [generatingImages, setGeneratingImages] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 12;
  
  // Calculate the current page's models
  const currentPageModels = useMemo(() => {
    const startIdx = (page - 1) * PAGE_SIZE;
    const endIdx = startIdx + PAGE_SIZE;
    return models.slice(startIdx, endIdx);
  }, [models, page, PAGE_SIZE]);
  
  // Function to check for Gcode files
  const checkForGcodeFiles = useCallback(async () => {
    try {
      setLoadingGcodeFiles(true);
      console.log(`[DEBUG] Checking for G-code files for project: ${projectName}`);
      
      // Make sure we have a valid project name
      if (!projectName || projectName.trim() === '') {
        console.warn('[DEBUG] Project name is empty, cannot check for G-code files');
        setGcodeFiles([]);
        setGcodeFolders([]);
        return;
      }
      
      // Get G-code files directly
      const result = await api.gcode.getProjectGCodeFiles(projectName);
      console.log(`[DEBUG] G-code files result:`, result);
      
      // Check if folders response exists and has content
      if (result.success && result.gcode_folders && result.gcode_folders.length > 0) {
        console.log(`[DEBUG] Found ${result.gcode_folders.length} G-code folders for project ${projectName}`);
        
        const folders = result.gcode_folders.map((folder: any): GCodeFolder => ({
          folder_name: folder.folder_name,
          readable_date: folder.readable_date,
          created_at: folder.created_at,
          files: folder.files.map((file: any) => ({
            id: `gcode_${file.filename}_${folder.folder_name}`,
            filename: file.filename,
            size: file.size,
            plateNumber: file.plate_number,
            download_url: file.download_url
          }))
        }));
        
        // Check for new folders and deduct credits based on timestamp
        const lastVisitTime = getLastVisitTime(projectName);
        const newFolders = folders.filter(folder => {
          // Parse the folder creation time, handle null/undefined
          if (!folder.created_at) return false;
          const folderCreatedAt = new Date(folder.created_at).getTime();
          return !isNaN(folderCreatedAt) && folderCreatedAt > lastVisitTime;
        });
        
        if (newFolders.length > 0) {
          console.log(`[DEBUG] Found ${newFolders.length} new G-code folders created after last visit (${new Date(lastVisitTime).toISOString()}), deducting credits`);
          
          try {
            // Calculate total plates in new folders
            const totalPlates = newFolders.reduce((sum, folder) => sum + folder.files.length, 0);
            
            if (totalPlates > 0) {
              // Deduct credits for new files
              await api.subscriptions.deductCreditsForGcode(totalPlates);
              
              // Update credits display in parent component
              if (onCreditsChanged) {
                onCreditsChanged();
              }
              
              console.log(`[DEBUG] Deducted ${totalPlates * 10} credits for ${totalPlates} new plates`);
            }
          } catch (error) {
            console.error("[DEBUG] Error deducting credits for new G-code files:", error);
            // Don't block the UI if credit deduction fails
          }
        } else {
          console.log(`[DEBUG] No new G-code folders found since last visit (${new Date(lastVisitTime).toISOString()})`);
        }
        
        setGcodeFolders(folders);
        
        // Clear gcodeFiles since we're using the folder structure
        setGcodeFiles([]);
      } 
      // Fallback for the old format
      else if (result.success && result.gcode_files && result.gcode_files.length > 0) {
        console.log(`[DEBUG] Found ${result.gcode_files.length} G-code files (old format) for project ${projectName}`);
        
        // Convert API response to GCodeFile format
        const gcodeFiles = result.gcode_files.map((file: { filename: string; size: number; plate_number: number; download_url: string }) => ({
          id: `gcode_${file.filename}_${Date.now()}`,
          filename: file.filename,
          size: file.size,
          plateNumber: file.plate_number,
          download_url: file.download_url
        }));
        
        setGcodeFiles(gcodeFiles);
        setGcodeFolders([]);
      } else {
        console.log(`[DEBUG] No G-code files found for project: ${projectName}`);
        setGcodeFiles([]);
        setGcodeFolders([]);
      }
    } catch (error) {
      console.error("[DEBUG] Error checking for G-code files:", error);
      setGcodeFiles([]);
      setGcodeFolders([]);
    } finally {
      setLoadingGcodeFiles(false);
    }
  }, [projectName]);
  
  // Check for existing G-code files when component mounts
  useEffect(() => {
    if (projectName) {
      console.log(`[DEBUG] Project mounted, checking for G-code files: ${projectName}`);
      checkForGcodeFiles();
    }
  }, [projectName, checkForGcodeFiles]);
  
  // Function to refresh all models in the project
  const refreshModels = async () => {
    try {
      if (onModelsProcessed) {
        onModelsProcessed(); // Trigger parent to refresh model list
      }
      
      // Also refresh GCode files
      await checkForGcodeFiles();
    } catch (error) {
      console.error("Error refreshing models:", error);
    }
  };
  
  // Toggle selection of a model
  const handleToggleSelect = useCallback((modelId: string) => {
    setSelectedModelIds(prev => {
      if (prev.includes(modelId)) {
        return prev.filter(id => id !== modelId);
      } else {
        return [...prev, modelId];
      }
    });
  }, []);

  // Toggle selection of all models
  const handleToggleSelectAll = useCallback(() => {
    if (selectedModelIds.length === models.length) {
      // If all are selected, unselect all
      setSelectedModelIds([]);
    } else {
      // Otherwise, select all
      setSelectedModelIds(models.map(model => model.id));
    }
  }, [models, selectedModelIds]);
  
  // Orientation popup handlers
  const handleOpenOrientationPopup = useCallback(() => {
    setShowOrientationPopup(true);
  }, []);
  
  const handleCloseOrientationPopup = useCallback(() => {
    setShowOrientationPopup(false);
  }, []);

  // Sequential popup handlers - show UserIntent first, then GCode settings
  const handleOpenGCodeGeneration = useCallback(() => {
    // Start with the UserIntent popup only
    setShowUserIntentPopup(true);
    setShowOrientationPopup(false);
  }, []);

  const handleCloseAllPopups = useCallback(() => {
    setShowOrientationPopup(false);
    setShowUserIntentPopup(false);
  }, []);

  // User intent popup handlers
  const handleUserIntentSubmit = useCallback((intent: UserIntent, customPrompt?: string, aiSuggestionData?: any) => {
    setSelectedUserIntent(intent);
    if (customPrompt) {
      setCustomPrompt(customPrompt);
    }
    if (aiSuggestionData) {
      setAiSuggestion(aiSuggestionData);
    }
    // Close UserIntent popup and open GCode settings popup
    setShowUserIntentPopup(false);
    setShowOrientationPopup(true);
  }, []);

  // Back button handler for GCode settings popup
  const handleBackToUserIntent = useCallback(() => {
    // Close GCode settings popup and reopen UserIntent popup
    setShowOrientationPopup(false);
    setShowUserIntentPopup(true);
  }, []);
  
  // Handle submit of orientation settings
  const handleSubmitOrientationSettings = async (settings: GCodeSettings) => {
    // Prevent multiple submissions
    if (isLoading || packingInProgress) {
      console.log("G-code generation already in progress, ignoring duplicate request");
      return;
    }
    
    console.log("Generate G-code clicked");
    console.log("Selected model IDs:", selectedModelIds);
    console.log("Export settings:", settings);
    
    // Check if any models are selected
    if (selectedModelIds.length === 0) {
      alert(t('gcodeGeneration.errors.selectModel'));
      setShowOrientationPopup(false);
      return;
    }
    
    // Check if user has sufficient credits
    if (currentCredits !== undefined && currentCredits !== null && currentCredits <= 0) {
      alert(t('gcodeGeneration.errors.insufficientCredits'));
      setShowOrientationPopup(false);
      setShowPricingPopup(true);
      return;
    }
    
    // Validate build plate size compatibility
    const buildPlateValidation = await validateBuildPlateSize(settings);
    if (buildPlateValidation.incompatibleModels.length > 0) {
      const selectedPrinter = `${settings.printer} ${settings.model}`;
      const canContinue = buildPlateValidation.incompatibleModels.length < selectedModelIds.length;
      
      // Show custom build plate warning popup
      setBuildPlateWarningData({
        printerName: selectedPrinter,
        buildPlateSize: buildPlateValidation.buildPlateSize,
        incompatibleModels: buildPlateValidation.incompatibleModels,
        canContinue: canContinue,
        pendingSettings: settings
      });
      setShowBuildPlateWarning(true);
      return; // Wait for user decision from popup
    }
    
    // Continue with G-code generation
    await continueGCodeGeneration(settings);
  };
  
  // Add deleteSelectedModels function
  const deleteSelectedModels = async () => {
    if (selectedModelIds.length === 0) return;
    
    // Calculate total storage space that will be recovered
    const selectedModels = models.filter(model => selectedModelIds.includes(model.id));
    const totalSize = selectedModels.reduce((sum, model) => sum + (model.size || 0), 0);
    
    // Format the total size for display

    
    // Create detailed confirmation message
    const fileList = selectedModels.map(model => `• ${model.filename}`).join('\n');
    const confirmMessage = selectedModelIds.length === 1 
      ? t('gcodeGeneration.confirm.deleteModels', {
          count: selectedModelIds.length,
          fileList,
          totalSize: formatSize(totalSize)
        })
      : t('gcodeGeneration.confirm.deleteModelsPlural', {
          count: selectedModelIds.length,
          fileList,
          totalSize: formatSize(totalSize)
        });
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      setIsLoading(true);
      
      let deletedCount = 0;
      let failedCount = 0;
      const failedFiles: string[] = [];
      
      // Delete each model
      for (const modelId of selectedModelIds) {
        try {
          await api.files.delete(modelId);
          deletedCount++;
          console.log(`Successfully deleted model: ${modelId}`);
        } catch (error) {
          failedCount++;
          const model = models.find(m => m.id === modelId);
          failedFiles.push(model?.filename || modelId);
          console.error(`Failed to delete model ${modelId}:`, error);
        }
      }
      
      // Clear selection
      setSelectedModelIds([]);
      
      // Show results
      if (deletedCount > 0) {
        const successMessage = deletedCount === 1
          ? t('gcodeGeneration.success.deleted', {
              count: deletedCount,
              size: formatSize(totalSize)
            })
          : t('gcodeGeneration.success.deletedPlural', {
              count: deletedCount,
              size: formatSize(totalSize)
            });
        
        if (failedCount > 0) {
          alert(`${successMessage}\n\nFailed to delete ${failedCount} file${failedCount > 1 ? 's' : ''}:\n• ${failedFiles.join('\n• ')}`);
        } else {
          alert(successMessage);
        }
        
        // Refresh the model list to update the UI
        if (onModelsProcessed) {
          onModelsProcessed();
        }
        
        // Refresh the page to ensure UI is fully updated
        setTimeout(() => {
          window.location.reload();
        }, 100);
      } else {
        alert(t('gcodeGeneration.errors.noFilesDeleted'));
      }
      
    } catch (error) {
      console.error('Error during deletion process:', error);
      alert(t('gcodeGeneration.errors.deletionFailed'));
    } finally {
      setIsLoading(false);
    }
  };
  
  // Validate if selected models will fit on the build plate
  const validateBuildPlateSize = async (settings: GCodeSettings) => {
    let buildPlateSize: [number, number];
    
    try {
      // Get dynamic bed dimensions from the API
      const bedDimensions = await api.printers.getBedDimensions(settings.printer, settings.model);
      buildPlateSize = [bedDimensions.width, bedDimensions.height];
      console.log(`Dynamic bed dimensions for ${settings.printer} ${settings.model}: ${buildPlateSize[0]}x${buildPlateSize[1]}`);
    } catch (error) {
      console.error('Error getting bed dimensions:', error);
      throw new Error(`Failed to get bed dimensions for ${settings.printer} ${settings.model}. Please try again or contact support.`);
    }
    
    const [maxWidth, maxHeight] = buildPlateSize;
    
    // Apply 5% margins to match the actual G-code generation process
    // Backend uses double-sided margins: 5% margin on each side = 10% total reduction
    const marginX = maxWidth * 0.05;   // 5% margin on each side  
    const marginY = maxHeight * 0.05;  // 5% margin on each side
    const usableWidth = maxWidth - (2 * marginX);   // Total: 10% reduction
    const usableHeight = maxHeight - (2 * marginY); // Total: 10% reduction
    const maxBuildPlateDimension = Math.max(maxWidth, maxHeight);
    
    // Check each selected model's dimensions
    const incompatibleModels: string[] = [];
    
    selectedModelIds.forEach(modelId => {
      const model = models.find(m => m.id === modelId);
      if (!model) return;
      
      // Get current dimensions (considering scaling)
      let modelDimensions = null;
      
      // Check if model has active version with scaled dimensions
      if (model.versions && model.activeVersionIndex !== undefined && model.versions[model.activeVersionIndex]) {
        modelDimensions = model.versions[model.activeVersionIndex].dimensions;
      } else if (model.originalDimensions) {
        modelDimensions = model.originalDimensions;
      }
      
      if (modelDimensions) {
        // Get all three dimensions
        const dimensions = [modelDimensions.x, modelDimensions.y, modelDimensions.z];
        dimensions.sort((a, b) => b - a); // Sort in descending order
        
        // Check if the two largest dimensions can fit within the build plate
        // (The model can be oriented so that the largest dimension is vertical if needed)
        const largestDim = dimensions[0];
        const secondLargestDim = dimensions[1];
        
        // For rectangular build plates, check if the two largest horizontal dimensions fit
        // Use the usable dimensions (with 5% tolerance) instead of full build plate size
        const canFitOriented = (largestDim <= usableWidth && secondLargestDim <= usableHeight) || 
                              (largestDim <= usableHeight && secondLargestDim <= usableWidth);
        
        if (!canFitOriented) {
          incompatibleModels.push(model.filename);
        }
      }
    });
    
    return {
      incompatibleModels,
      buildPlateSize: `${maxWidth}x${maxHeight}`,
      maxDimension: maxBuildPlateDimension
    };
  };
  
  // Build plate warning popup handlers
  const handleBuildPlateWarningClose = () => {
    setShowBuildPlateWarning(false);
    setBuildPlateWarningData(null);
    setIsLoading(false);
    setPackingInProgress(false);
  };
  
  const handleBuildPlateWarningContinue = async () => {
    if (!buildPlateWarningData?.pendingSettings) return;
    
    setShowBuildPlateWarning(false);
    setBuildPlateWarningData(null);
    
    // Continue with G-code generation using the pending settings
    // We need to continue the rest of the handleSubmitOrientationSettings logic
    await continueGCodeGeneration(buildPlateWarningData.pendingSettings);
  };
  
  // Extract the G-code generation logic to be reusable
  const continueGCodeGeneration = async (settings: GCodeSettings) => {
    setIsLoading(true);
    setPackingInProgress(true);
    
    try {
      // Prepare the selected models with their duplicate counts and scale factors
      const selectedModels = models
        .filter(model => selectedModelIds.includes(model.id))
        .map(model => {
          // Get the current active version for scale factor
          const activeVersionIndex = model.activeVersionIndex ?? 0;
          const scaleFactor = model.versions?.[activeVersionIndex]?.scale ?? 1.0;
          
          return {
            id: model.id,
            duplicates: model.duplicates || 1,
            scale: scaleFactor
          };
        });
        
      console.log("Selected models with duplicates and scale factors:", selectedModels);
      
      // Call the arranged G-code generation API
      console.log(`Generating G-code for ${selectedModelIds.length} models with settings:`, settings);
      console.log(`User intent: ${selectedUserIntent}, Custom prompt:`, customPrompt);
      console.log(`AI suggestion:`, aiSuggestion);
      
      // Extract custom parameters from AI suggestion if available
      const customParameters = aiSuggestion?.custom_parameters;
      
      const gcodeResponse = await api.gcode.generateArrangedGcode(selectedModels, settings, selectedUserIntent, customParameters);
      console.log("G-code generation API response:", gcodeResponse);
      
      // Check for success and the presence of individual G-code files
      if (gcodeResponse.success) {
        // No credits deducted during generation - they're deducted when files appear
        
        let plateCount = gcodeResponse.plate_count || 0;
        
        // If individual G-code files are returned, use them
        if (gcodeResponse.gcode_files && gcodeResponse.gcode_files.length > 0) {
          console.log(`Successfully generated ${gcodeResponse.gcode_files.length} G-code files in folder ${gcodeResponse.timestamp_folder || 'unknown'}`);
          
          // Convert API response to GCodeFile format
          const gcodeFiles = gcodeResponse.gcode_files.map((file: { filename: string; size: number; plate_number: number; download_url: string }) => ({
            id: `gcode_${file.filename}_${Date.now()}`,
            filename: file.filename,
            size: file.size,
            plateNumber: file.plate_number,
            download_url: file.download_url
          }));
          
          // Only use folders structure, not both gcodeFiles and gcodeFolders
          // Create a folder for these files
          if (gcodeResponse.timestamp_folder) {
            const folder: GCodeFolder = {
              folder_name: gcodeResponse.timestamp_folder,
              readable_date: new Date().toLocaleString(),
              created_at: new Date().toISOString(),
              files: gcodeFiles
            };
            
            setGcodeFolders([folder, ...gcodeFolders]);
            // Clear the old gcodeFiles since we're using folders now
            setGcodeFiles([]);
          } else {
            // Fallback: if no timestamp folder, use the old gcodeFiles approach
            setGcodeFiles(gcodeFiles);
          }
          
          plateCount = gcodeFiles.length;
          
          // Show success message - credits will be deducted when files appear in the G-code section
          alert(t('gcodeGeneration.success.generated', { 
            plateCount, 
            folderName: gcodeResponse.timestamp_folder || 'unknown' 
          }));
        } else if (gcodeResponse.job_completed) {
          // Job completed but we need to fetch the files
          alert(t('gcodeGeneration.success.completed'));
          
          // Refresh models to trigger a reload of G-code files
          refreshModels();
          
          // Wait a moment then check for files and switch to G-code tab
          setTimeout(async () => {
            try {
              console.log('[DEBUG] Checking for G-code files after job completion...');
              await checkForGcodeFiles();
              // Switch to G-code tab if we found files
              setActiveTab('gcode');
            } catch (error) {
              console.error('[DEBUG] Error checking for G-code files:', error);
            }
          }, 2000);
        } else {
          // No individual files available and no job completion info
          alert(t('gcodeGeneration.errors.noFiles'));
        }
        
        // Refresh models to show updated versions if needed
        refreshModels();
        
        // If we successfully added files to folders, switch to G-code tab immediately
        if (gcodeResponse.timestamp_folder) {
          setActiveTab('gcode');
        }
      } else {
        // G-code generation failed
        if (gcodeResponse.error) {
          // Check if it's an insufficient credits error
          if (gcodeResponse.error.toLowerCase().includes('insufficient credits')) {
            setShowOrientationPopup(false);
            setIsLoading(false);
            setPackingInProgress(false);
            
            // Show upgrade prompt directly as popup instead of window.confirm
            setShowPricingPopup(true);
            return;
          }
          
          // Check if it's a plate limit exceeded error requiring upgrade
          if (gcodeResponse.upgrade_required) {
            setShowOrientationPopup(false);
            setIsLoading(false);
            setPackingInProgress(false);
            
            // Create a detailed upgrade message
            let upgradeMessage = gcodeResponse.error;
            if (gcodeResponse.upgrade_message) {
              upgradeMessage += `\n\n${gcodeResponse.upgrade_message}`;
            }
            if (gcodeResponse.upgrade_benefits) {
              upgradeMessage += `\n\n${gcodeResponse.upgrade_benefits}`;
            }
            if (gcodeResponse.action_required) {
              upgradeMessage += `\n\n${gcodeResponse.action_required}`;
            }
            
            // Add file information if available
            if (gcodeResponse.unprocessed_files && gcodeResponse.unprocessed_files.length > 0) {
              const unprocessedList = gcodeResponse.unprocessed_files
                .filter((file: string) => file && !file.includes("Unable to identify"))
                .map((file: string) => `• ${file}`)
                .join('\n');
              
              if (unprocessedList) {
                upgradeMessage += `\n\n📄 Files that were NOT processed:\n${unprocessedList}`;
              }
            }
            
            if (gcodeResponse.processed_files && gcodeResponse.processed_files.length > 0) {
              const processedList = gcodeResponse.processed_files
                .filter((file: string) => file && !file.includes("Unable to identify"))
                .map((file: string) => `• ${file}`)
                .join('\n');
              
              if (processedList) {
                upgradeMessage += `\n\n✅ Files that were processed:\n${processedList}`;
              }
            }
            
            // Show upgrade message and then open pricing popup
            alert(upgradeMessage);
            setShowPricingPopup(true);
            return;
          }
          
          alert(t('gcodeGeneration.errors.serverError', { error: gcodeResponse.error }));
        } else {
          alert(t('gcodeGeneration.errors.generationFailed'));
        }
      }
    } catch (error: any) {
      console.error("Error generating G-code:", error);
      
      // If the error is about insufficient credits, show pricing popup
      if (error?.response?.data?.detail?.includes('insufficient credits')) {
        setShowOrientationPopup(false);
        setIsLoading(false);
        setPackingInProgress(false);
        setShowPricingPopup(true);
        return;
      }
      
      let errorMessage = t('gcodeGeneration.errors.generationFailed');
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    } finally {
      setIsLoading(false);
      setPackingInProgress(false);
      setShowOrientationPopup(false);
    }
  };
  
  const [showPricingPopup, setShowPricingPopup] = useState(false);
  
  // Build plate warning popup state
  const [showBuildPlateWarning, setShowBuildPlateWarning] = useState(false);
  const [buildPlateWarningData, setBuildPlateWarningData] = useState<{
    printerName: string;
    buildPlateSize: string;
    incompatibleModels: string[];
    canContinue: boolean;
    pendingSettings?: GCodeSettings;
  } | null>(null);
  
  // Handle file selection for adding new models
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    // Check each file for size limit (100MB = 100 * 1024 * 1024 bytes)
    const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB in bytes
    const oversizedFiles: string[] = [];
    const validFiles: File[] = [];
    
    Array.from(files).forEach(file => {
      const fileSizeMB = file.size / (1024 * 1024);
      
      if (file.size > MAX_FILE_SIZE) {
        oversizedFiles.push(`${file.name} (${fileSizeMB.toFixed(1)} MB)`);
      } else {
        validFiles.push(file);
      }
    });
    
    if (oversizedFiles.length > 0) {
      const errorMessage = `${t('gcodeGeneration.errors.uploadSizeFailed')}\n\n${oversizedFiles.join('\n')}`;
      alert(errorMessage);
    }
    
    setSelectedFiles(validFiles);
    
    // If we have valid files, start the upload process
    if (validFiles.length > 0) {
      handleUploadFiles(validFiles);
    }
    
    // Reset the file input
    if (event.target) {
      event.target.value = '';
    }
  };

  // Handle uploading files to the current project with auto-orientation
  const handleUploadFiles = async (filesToUpload: File[]) => {
    if (filesToUpload.length === 0) return;

    setIsUploading(true);
    setSelectedFiles(filesToUpload);
    
    // Initialize progress tracking
    const initialProgress = Object.fromEntries(
      filesToUpload.map(file => [file.name, 0])
    );
    setUploadProgress(initialProgress);

    try {
      // Upload each file
      const uploadPromises = filesToUpload.map(async (file) => {
        try {
          // Create XMLHttpRequest to track upload progress
          const xhr = new XMLHttpRequest();
          const formData = new FormData();
          formData.append('file', file);
          formData.append('project_name', projectName);
          
          // Create a promise that will resolve when the upload is complete
          const uploadPromise = new Promise<any>((resolve, reject) => {
            xhr.upload.addEventListener('progress', (event) => {
              if (event.lengthComputable) {
                const progress = Math.round((event.loaded / event.total) * 100);
                setUploadProgress(prev => ({
                  ...prev,
                  [file.name]: progress
                }));
              }
            });
            
            xhr.addEventListener('load', () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                try {
                  const response = JSON.parse(xhr.responseText);
                  resolve(response);
                } catch (e) {
                  reject(new Error('Invalid response from server'));
                }
              } else {
                try {
                  const errorData = JSON.parse(xhr.responseText);
                  const errorMessage = errorData.detail || 'Upload failed';
                  reject(new Error(errorMessage));
                } catch (e) {
                  reject(new Error(`Upload failed with status ${xhr.status}`));
                }
              }
            });
            
            xhr.addEventListener('error', () => {
              reject(new Error('Network error during upload'));
            });
          });
          
          // Get auth token and set headers
          const token = await auth.currentUser?.getIdToken();
          
          // Use the same URL logic as the main upload form
          const hostname = window.location.hostname;
          let uploadUrl = '';
          if (hostname === 'localhost') {
            uploadUrl = 'http://localhost:8001/api/v1/files/upload';
          } else if (hostname === 'staging.maxel.ai') {
            uploadUrl = '/api/v1/files/upload';
          } else {
            uploadUrl = '/api/v1/files/upload';
          }
          
          xhr.open('POST', uploadUrl);
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          
          // Start the upload
          xhr.send(formData);
          
          return await uploadPromise;
        } catch (error) {
          console.error(`Error uploading ${file.name}:`, error);
          throw error;
        }
      });

      const results = await Promise.allSettled(uploadPromises);
      
      // Filter successful uploads
      const successfulUploads = results
        .filter((result): result is PromiseFulfilledResult<any> => result.status === 'fulfilled')
        .map(result => result.value);
      
      // Process successful uploads for auto-orientation
      if (successfulUploads.length > 0) {
        const fileIds = successfulUploads.map(file => file.id);
        
        // Apply auto-orientation with default settings
        try {
          console.log(`Applying auto-orientation to ${fileIds.length} uploaded files`);
          
          // Use default orientation settings
          const orientationSettings = {
            printer: "prusa_mk4s",
            nozzle: "0.4",
            material: "pla"
          };
          
          await api.orientation.process(fileIds, orientationSettings, false);
          console.log('Auto-orientation completed successfully');
          
        } catch (orientationError) {
          console.error('Error during auto-orientation:', orientationError);
          // Continue even if orientation fails
        }
      }
      
      // Check for any failures
      const failures = results.filter(result => result.status === 'rejected');
      if (failures.length > 0) {
        alert(t('gcodeGeneration.errors.uploadFailed').replace('{{count}}', failures.length.toString()));
      }
      
      if (successfulUploads.length > 0) {
        const message = successfulUploads.length === 1
          ? t('gcodeGeneration.success.uploaded', {
              count: successfulUploads.length,
              projectName
            })
          : t('gcodeGeneration.success.uploadedPlural', {
              count: successfulUploads.length,
              projectName
            });
        alert(message);
        
        // Refresh the model list to show new uploads
        if (onModelsProcessed) {
          onModelsProcessed();
        }
        
        // Refresh the page to ensure UI is fully updated
        setTimeout(() => {
          window.location.reload();
        }, 100);
      }
      
    } catch (error) {
      console.error('Error during upload process:', error);
      alert(t('gcodeGeneration.errors.uploadFailed'));
    } finally {
      setIsUploading(false);
      setSelectedFiles([]);
      setUploadProgress({});
    }
  };

  // Trigger file input click
  const handleAddModelsClick = () => {
    fileInputRef.current?.click();
  };

  if (loading || processingOrientation) {
    return (
      <div className="spinner-container">
        <div className="classic-spinner"></div>
        <h2 className="spinner-title">{t('projectPreview.loading.projectModels')}</h2>
        <p className="spinner-subtitle">{processingOrientation ? t('projectPreview.loading.optimizing') : t('projectPreview.loading.preparing')}</p>
        <p className="spinner-text">{processingOrientation ? t('projectPreview.loading.preparingModels') : t('projectPreview.loading.loadingModels')}</p>
      </div>
    );
  }

  // Show loading state with cancel button during packing
  if (isLoading && packingInProgress) {
    return (
      <div className="spinner-container">
        <div className="classic-spinner"></div>
        <h2 className="spinner-title">{t('projectPreview.loading.generatingGcode')}</h2>
        <p className="spinner-subtitle">{t('projectPreview.loading.optimizingModels')}</p>
        <p className="spinner-text">{t('projectPreview.loading.processTime')}</p>
      </div>
    );
  }

  if (models.length === 0) {
    return (
      <div className="project-preview empty">
        <h2>{t('projectPreview.emptyProject.title', { projectName })}</h2>
        <p>{t('projectPreview.emptyProject.message')}</p>
      </div>
    );
  }

  return (
    <ModelCacheContext.Provider value={{ cache: modelCache, addToCache: (id, url, scene) => {
      if (!id || !url || !scene) return;
      
      console.log(`Adding model ${id} to persistent cache`);
      if (!modelCache.models[id]) {
        modelCache.models[id] = scene.clone();
        modelCache.urls[id] = url;
      }
    } }}>
      <div className="project-preview-container">
        {/* Tabs navigation with Generate G-code button */}
        <div className="project-tabs-container">
          <div className="project-tabs">
            <button 
              className={`tab-button ${activeTab === 'models' ? 'active' : ''}`}
              onClick={() => setActiveTab('models')}
            >
              {t('projectPreview.tabs.models')} ({models.length})
            </button>
            <button 
              className={`tab-button ${activeTab === 'gcode' ? 'active' : ''}`}
              onClick={() => setActiveTab('gcode')}
              disabled={gcodeFiles.length === 0 && gcodeFolders.length === 0 && !loadingGcodeFiles}
            >
              {t('projectPreview.tabs.gcode')} ({gcodeFiles.length + gcodeFolders.reduce((sum, folder) => sum + folder.files.length, 0)})
              {loadingGcodeFiles && <span className="loading-dot"></span>}
            </button>
          </div>
          
          <div className="project-actions">
            {activeTab === 'models' && (
              <>
                <button 
                  className="select-all-btn"
                  onClick={handleToggleSelectAll}
                  title={selectedModelIds.length === models.length ? t('projectPreview.actions.tooltips.deselectAllModels') : t('projectPreview.actions.tooltips.selectAllModels')}
                >
                  {selectedModelIds.length === models.length ? t('projectPreview.actions.deselectAll') : t('projectPreview.actions.selectAll')}
                </button>
                <button 
                  className="add-models-btn"
                  onClick={handleAddModelsClick}
                  disabled={isUploading}
                  title={t('projectPreview.actions.tooltips.addModels')}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 5V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <button 
                  className="delete-selected-btn"
                  disabled={selectedModelIds.length === 0}
                  onClick={deleteSelectedModels}
                  title={selectedModelIds.length === 0 ? t('projectPreview.actions.tooltips.selectFirst') : t('projectPreview.actions.tooltips.deleteSelected')}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                
                {/* Hidden file input for adding models */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".stl,.obj,.3mf"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />
              </>
            )}
            <button 
              className="auto-orient-btn"
              disabled={selectedModelIds.length === 0 || (currentCredits !== undefined && currentCredits !== null && currentCredits <= 0) || isLoading || packingInProgress}
              onClick={handleOpenGCodeGeneration}
              title={
                selectedModelIds.length === 0 
                  ? t('projectPreview.actions.tooltips.selectFirst')
                  : (currentCredits !== undefined && currentCredits !== null && currentCredits <= 0)
                    ? t('projectPreview.actions.tooltips.insufficientCredits')
                    : (isLoading || packingInProgress)
                      ? t('projectPreview.loading.generatingGcode')
                      : t('projectPreview.actions.tooltips.generateGcodeTooltip')
              }
            >
              {t('projectPreview.actions.generateGcode')}
              {currentCredits !== undefined && currentCredits !== null && currentCredits <= 0 && (
                <span className="insufficient-credits-badge">{t('projectPreview.actions.noCredits')}</span>
              )}
            </button>
          </div>
        </div>

        {/* Tab content */}
        <div className="tab-content">
          {/* 3D Models Tab */}
          {activeTab === 'models' && (
            <>
              {/* Upload progress display */}
              {isUploading && selectedFiles.length > 0 && (
                <div className="upload-progress-container">
                  <h3>{selectedFiles.length > 1 ? t('projectPreview.upload.titlePlural', { count: selectedFiles.length, projectName }) : t('projectPreview.upload.title', { count: selectedFiles.length, projectName })}</h3>
                  <div className="upload-files-list">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="upload-file-item">
                        <div className="file-info">
                          <div className="file-name">{file.name}</div>
                          <div className="file-size">{(file.size / 1024 / 1024).toFixed(1)} MB</div>
                        </div>
                        <div className="upload-progress">
                          <div 
                            className="upload-progress-bar" 
                            style={{ width: `${uploadProgress[file.name] || 0}%` }}
                          ></div>
                          <span className="upload-progress-text">{uploadProgress[file.name] || 0}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="upload-note">{t('projectPreview.upload.note')}</p>
                </div>
              )}
              
              <div className="model-grid">
                {currentPageModels.map(model => (
                  <ModelCard 
                    key={model.id} 
                    model={model} 
                    isSelected={selectedModelIds.includes(model.id)}
                    onToggleSelect={() => handleToggleSelect(model.id)}
                  />
                ))}
              </div>
              
              {/* Pagination controls */}
              {models.length > PAGE_SIZE && (
                <div className="pagination">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="pagination-button"
                  >
                    {t('projectPreview.pagination.previous')}
                  </button>
                  <span className="page-info">
                    {t('projectPreview.pagination.pageInfo', { current: page, total: Math.ceil(models.length / PAGE_SIZE) })}
                  </span>
                  <button
                    onClick={() => setPage(Math.min(Math.ceil(models.length / PAGE_SIZE), page + 1))}
                    disabled={page === Math.ceil(models.length / PAGE_SIZE)}
                    className="pagination-button"
                  >
                    {t('projectPreview.pagination.next')}
                  </button>
                </div>
              )}
            </>
          )}

          {/* G-Code Files Tab */}
          {activeTab === 'gcode' && (
            <div className="gcode-tab-content">
              {loadingGcodeFiles ? (
                <div className="gcode-loading">
                  <p>{t('projectPreview.gcode.fetching')}</p>
                  <div className="classic-spinner-small"></div>
                </div>
              ) : gcodeFolders.length > 0 ? (
                <div className="gcode-folders-list">
                  {gcodeFolders.map((folder) => (
                    <GCodeFolderCard 
                      key={folder.folder_name} 
                      folder={{
                        ...folder,
                        project_id: projectName
                      }}
                      onDelete={(deletedFolder) => {
                        // Remove the deleted folder from the state
                        setGcodeFolders(prev => 
                          prev.filter(f => f.folder_name !== deletedFolder.folder_name)
                        );
                      }}
                    />
                  ))}
                </div>
              ) : gcodeFiles.length > 0 ? (
                // Fallback for old structure (no folders)
                <div className="gcode-grid">
                  {gcodeFiles.map((gcodeFile: GCodeFile) => (
                    <GCodePreviewCard 
                      key={gcodeFile.id} 
                      file={gcodeFile} 
                    />
                  ))}
                </div>
              ) : (
                <div className="empty-gcode-message">
                  <p>{t('projectPreview.gcode.empty')}</p>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Auto Orientation Popup */}
        <GCodeSettingsPopup 
          isOpen={showOrientationPopup}
          onClose={handleCloseAllPopups}
          onSubmit={handleSubmitOrientationSettings}
          onBack={handleBackToUserIntent}
        />
        
        {/* User Intent Popup */}
        <UserIntentPopup 
          isOpen={showUserIntentPopup}
          onClose={handleCloseAllPopups}
          onSubmit={handleUserIntentSubmit}
        />
        
        {/* Pricing Popup */}
        <PricingPopup 
          isOpen={showPricingPopup}
          onClose={() => setShowPricingPopup(false)}
          onPlanChange={() => {
            // Refresh credits display after plan change
            if (onCreditsChanged) {
              onCreditsChanged();
            }
          }}
        />
        
        {/* Build Plate Warning Popup */}
        {buildPlateWarningData && (
          <BuildPlateWarningPopup 
            isOpen={showBuildPlateWarning}
            onClose={handleBuildPlateWarningClose}
            onContinue={buildPlateWarningData.canContinue ? handleBuildPlateWarningContinue : undefined}
            printerName={buildPlateWarningData.printerName}
            buildPlateSize={buildPlateWarningData.buildPlateSize}
            incompatibleModels={buildPlateWarningData.incompatibleModels}
            canContinue={buildPlateWarningData.canContinue}
          />
        )}
      </div>
    </ModelCacheContext.Provider>
  );
}

export default ProjectPreview; 